import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpResponse,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, mergeMap, materialize, dematerialize } from 'rxjs/operators';

/**
 * Simulates legacy /users demo API only. Real CRM requests must not be delayed.
 */
@Injectable()
export class FakeBackendInterceptor implements HttpInterceptor {

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const fake$ = this.tryHandleFakeUsersApi(request);
        if (fake$ !== null) {
            return fake$.pipe(
                materialize(),
                delay(500),
                dematerialize()
            );
        }
        return next.handle(request);
    }

    /** Returns an observable for handled fake routes, or null to pass through to the real backend. */
    private tryHandleFakeUsersApi(request: HttpRequest<any>): Observable<HttpEvent<any>> | null {
        const users: any[] = JSON.parse(localStorage.getItem('users')) || [{
            username: 'admin',
            email: 'admin@themesbrand.com',
            password: '123456'
        }];

        if (request.url.endsWith('/users/authenticate') && request.method === 'POST') {
            return of(null).pipe(mergeMap(() => {
                const filteredUsers = users.filter(user => {
                    return user.email === request.body.email && user.password === request.body.password;
                });
                if (filteredUsers.length) {
                    const user = filteredUsers[0];
                    const body = {
                        id: user.id,
                        email: user.email,
                        username: user.username,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        token: 'fake-jwt-token'
                    };
                    return of(new HttpResponse({ status: 200, body }));
                }
                return throwError(() => ({ error: { message: 'Username or password is incorrect' } }));
            }));
        }

        if (request.url.endsWith('/users') && request.method === 'GET') {
            return of(null).pipe(mergeMap(() => {
                if (request.headers.get('Authorization') === 'Bearer fake-jwt-token') {
                    return of(new HttpResponse({ status: 200, body: users }));
                }
                return throwError(() => ({ status: 401, error: { message: 'Unauthorised' } }));
            }));
        }

        if (request.url.match(/\/users\/\d+$/) && request.method === 'GET') {
            return of(null).pipe(mergeMap(() => {
                if (request.headers.get('Authorization') === 'Bearer fake-jwt-token') {
                    const urlParts = request.url.split('/');
                    const id = parseInt(urlParts[urlParts.length - 1], 10);
                    const matchedUsers = users.filter(user => user.id === id);
                    const user = matchedUsers.length ? matchedUsers[0] : null;
                    return of(new HttpResponse({ status: 200, body: user }));
                }
                return throwError(() => ({ status: 401, error: { message: 'Unauthorised' } }));
            }));
        }

        if (request.url.endsWith('/users/register') && request.method === 'POST') {
            return of(null).pipe(mergeMap(() => {
                const newUser = request.body;
                const duplicateUser = users.filter(u => u.username === newUser.username).length;
                if (duplicateUser) {
                    return throwError(() => ({
                        error: { message: 'Username "' + newUser.username + '" is already taken' }
                    }));
                }
                newUser.id = users.length + 1;
                users.push(newUser);
                localStorage.setItem('users', JSON.stringify(users));
                return of(new HttpResponse({ status: 200 }));
            }));
        }

        if (request.url.match(/\/users\/\d+$/) && request.method === 'DELETE') {
            return of(null).pipe(mergeMap(() => {
                if (request.headers.get('Authorization') !== 'Bearer fake-jwt-token') {
                    return throwError(() => ({ status: 401, error: { message: 'Unauthorised' } }));
                }
                const urlParts = request.url.split('/');
                const id = parseInt(urlParts[urlParts.length - 1], 10);
                for (let i = 0; i < users.length; i++) {
                    if (users[i].id === id) {
                        users.splice(i, 1);
                        localStorage.setItem('users', JSON.stringify(users));
                        break;
                    }
                }
                return of(new HttpResponse({ status: 200 }));
            }));
        }

        return null;
    }
}
